import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import type {
  AppSettings,
  CreateInviteResponse,
  InviteSummary,
  ProjectSummary,
} from '@blesk/contracts';
import { apiGet, apiPost, apiPut } from '../../shared/api';
import { useProjectOptions } from '../../shared/options';
import { usePagedList } from '../../shared/lists';
import { CHANNEL_LABEL, INVITE_STATUS_LABEL } from '../../shared/labels';
import { formatInProjectTz } from '../../shared/format';
import { useHasPermission, useSession } from '../../shared/session';
import { useUrlFilter } from '../../shared/filters';
import { PermissionGate } from '../shell/PermissionGate';
import {
  Dialog,
  EmptyRow,
  ErrorNote,
  ExportCsvLink,
  Pager,
  SecretValue,
  orDash,
  useConfirm,
} from '../shell/ui';

/** §7.1, §13 ТЗ, E5. Раздел «Приглашения». */
export function InvitesPage() {
  return (
    <PermissionGate permission="invites.read">
      <InvitesInner />
    </PermissionGate>
  );
}

function InvitesInner() {
  const session = useSession();
  const canWrite = useHasPermission('invites.write');
  const queryClient = useQueryClient();

  const [projectId, setProjectId] = useUrlFilter('projectId');
  const [status, setStatus] = useUrlFilter('status');
  const [createOpen, setCreateOpen] = useState(false);
  const confirmation = useConfirm();
  const [secrets, setSecrets] = useState<CreateInviteResponse | null>(null);

  const projects = useProjectOptions();
  const list = usePagedList<InviteSummary>('invites', '/api/invites', { projectId, status });
  const invalidate = () => queryClient.invalidateQueries({ queryKey: ['invites'] });

  const revoke = useMutation({
    mutationFn: (id: string) => apiPost(`/api/invites/${id}/revoke`),
    onSuccess: invalidate,
  });

  const timezoneOf = (invite: InviteSummary) =>
    projects.data?.find((p) => p.id === invite.projectId)?.timezone ?? session.defaultTimezone;

  return (
    <>
      <h1>Приглашения</h1>
      <p className="muted">
        Токен и PIN показываются один раз при создании; повторно — только новое приглашение.
      </p>

      <ConsentSetting />

      <div className="toolbar">
        <label>
          Объект
          <select value={projectId} onChange={(e) => setProjectId(e.target.value)}>
            <option value="">Все</option>
            {(projects.data ?? []).map((project) => (
              <option key={project.id} value={project.id}>
                {project.name}
              </option>
            ))}
          </select>
        </label>
        <label>
          Статус
          <select value={status} onChange={(e) => setStatus(e.target.value)}>
            <option value="">Все</option>
            {Object.entries(INVITE_STATUS_LABEL).map(([value, label]) => (
              <option key={value} value={value}>
                {label}
              </option>
            ))}
          </select>
        </label>
        <span className="toolbar__spacer" />
        {/* E5: выгрузка текущего отбора — рассылку ведёт заказчик своими силами */}
        <ExportCsvLink path="/api/invites/export" filters={{ projectId, status }} />
        {canWrite && (
          <button type="button" className="btn-primary" onClick={() => setCreateOpen(true)}>
            Создать приглашение
          </button>
        )}
      </div>
      <ErrorNote error={list.error ?? revoke.error} />

      <div className="table-wrap">
        <table className="table">
          <thead>
            <tr>
              <th>Объект</th>
              <th>Статус</th>
              <th>Автор</th>
              <th>Действует до</th>
              <th>Попытки PIN</th>
              <th>Кем использовано</th>
              {canWrite && <th>Действия</th>}
            </tr>
          </thead>
          <tbody>
            {list.items.length === 0 && (
              <EmptyRow colSpan={canWrite ? 7 : 6} loading={list.isPending} />
            )}
            {list.items.map((invite) => (
              <tr key={invite.id}>
                <td>{invite.projectName}</td>
                <td>{INVITE_STATUS_LABEL[invite.status]}</td>
                <td>{invite.createdByName}</td>
                <td>{formatInProjectTz(invite.expiresAt, timezoneOf(invite))}</td>
                <td>
                  {invite.attempts} из {invite.maxAttempts}
                </td>
                <td>{orDash(invite.usedByName)}</td>
                {canWrite && (
                  <td>
                    {invite.status === 'ACTIVE' && (
                      <button
                        type="button"
                        className="btn-danger"
                        disabled={revoke.isPending}
                        onClick={() =>
                          confirmation.ask({
                            title: 'Отзыв приглашения',
                            question: `Отозвать приглашение для «${invite.projectName}»?`,
                            warning:
                              'Ссылка и PIN перестанут работать; выпустить можно только новое.',
                            verb: 'Отозвать',
                            danger: true,
                            onConfirm: () => revoke.mutate(invite.id),
                          })
                        }
                      >
                        Отозвать
                      </button>
                    )}
                  </td>
                )}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      <Pager page={list.page} perPage={list.perPage} total={list.total} onPage={list.setPage} />

      {createOpen && (
        <CreateInviteDialog
          projects={projects.data ?? []}
          onClose={() => setCreateOpen(false)}
          onCreated={(response) => {
            setCreateOpen(false);
            setSecrets(response);
            invalidate();
          }}
        />
      )}
      {secrets && <SecretsDialog response={secrets} onClose={() => setSecrets(null)} />}
      {confirmation.dialog}
    </>
  );
}

/**
 * §7.2, §24.1 ТЗ. Текст согласия на обработку персональных данных передаёт
 * заказчик, и до тех пор регистрация не запускается: бот доводит человека до
 * последнего шага и отвечает «Регистрация временно недоступна».
 *
 * Раздел здесь, а не в отдельных настройках, потому что именно здесь выпускают
 * приглашения: узнать о пустом тексте от представителя заказчика, который уже
 * ввёл PIN и ФИО, — худший из способов. Текст правит тот, кто может менять
 * анкеты (§3), остальные видят только предупреждение.
 */
function ConsentSetting() {
  const canEdit = useHasPermission('surveys.write');
  const queryClient = useQueryClient();
  const [draft, setDraft] = useState<string | null>(null);

  const settings = useQuery({
    queryKey: ['settings'],
    queryFn: () => apiGet<AppSettings>('/api/settings'),
    staleTime: 5 * 60 * 1000,
  });

  const save = useMutation({
    mutationFn: (value: string) => apiPut('/api/settings/consent_text', { value }),
    onSuccess: () => {
      setDraft(null);
      void queryClient.invalidateQueries({ queryKey: ['settings'] });
    },
  });

  const text = settings.data?.consent_text ?? '';
  if (settings.isPending) return null;
  if (text && draft === null) {
    return (
      <p className="muted">
        Текст согласия на обработку персональных данных заполнен.{' '}
        {canEdit && (
          <button type="button" className="link-button" onClick={() => setDraft(text)}>
            Изменить
          </button>
        )}
      </p>
    );
  }

  return (
    <section className="notice">
      {!text && (
        <p className="error" role="alert">
          Текст согласия на обработку персональных данных не заполнен — регистрация по
          приглашениям не запустится (§7.2). Бот доведёт человека до последнего шага и ответит
          «Регистрация временно недоступна».
        </p>
      )}
      {canEdit ? (
        <>
          <label htmlFor="consent-text">Текст согласия</label>
          <textarea
            id="consent-text"
            rows={4}
            value={draft ?? ''}
            onChange={(event) => setDraft(event.target.value)}
            placeholder="Текст передаёт заказчик (§24.1)"
          />
          <ErrorNote error={save.error} />
          <div className="form__actions">
            {text && (
              <button type="button" onClick={() => setDraft(null)}>
                Отмена
              </button>
            )}
            <button
              type="button"
              className="btn-primary"
              disabled={save.isPending || !(draft ?? '').trim()}
              onClick={() => save.mutate((draft ?? '').trim())}
            >
              Сохранить
            </button>
          </div>
          <p className="muted">
            Снимок текста сохраняется у каждого согласия: правка не меняет уже собранные (§7.2).
          </p>
        </>
      ) : (
        <p className="muted">Заполнить его может системный администратор.</p>
      )}
    </section>
  );
}

function CreateInviteDialog({
  projects,
  onClose,
  onCreated,
}: {
  projects: ProjectSummary[];
  onClose: () => void;
  onCreated: (response: CreateInviteResponse) => void;
}) {
  const [projectId, setProjectId] = useState('');
  const [ttlHours, setTtlHours] = useState('');

  // «Пусто — по настройке» не отвечает на вопрос «а сколько это»: подставляем
  // действующее значение. Право на чтение настроек есть у всех, кто может
  // выпускать приглашения, но запрос всё равно необязательный — без него
  // остаётся общая формулировка, а не пустое место.
  const settings = useQuery({
    queryKey: ['settings'],
    queryFn: () => apiGet<AppSettings>('/api/settings'),
    staleTime: 5 * 60 * 1000,
  });
  const defaultTtl = settings.data?.invite_ttl_hours;

  const create = useMutation({
    mutationFn: () =>
      apiPost<CreateInviteResponse>('/api/invites', {
        projectId,
        ...(ttlHours ? { ttlHours: Number(ttlHours) } : {}),
      }),
    onSuccess: onCreated,
  });

  return (
    <Dialog title="Новое приглашение" onClose={onClose}>
      <form
        onSubmit={(e) => {
          e.preventDefault();
          create.mutate();
        }}
      >
        <label>
          Объект
          <select value={projectId} onChange={(e) => setProjectId(e.target.value)} required>
            <option value="">— выберите —</option>
            {projects.map((project) => (
              <option key={project.id} value={project.id}>
                {project.name}
              </option>
            ))}
          </select>
        </label>
        <label>
          {defaultTtl
            ? `Срок действия, часов (пусто — ${defaultTtl} ч по настройке)`
            : 'Срок действия, часов (пусто — по общей настройке)'}
          <input
            type="number"
            min={1}
            max={720}
            value={ttlHours}
            onChange={(e) => setTtlHours(e.target.value)}
          />
        </label>
        <ErrorNote error={create.error} />
        <div className="form__actions">
          <button type="button" onClick={onClose}>
            Отмена
          </button>
          <button type="submit" className="btn-primary" disabled={create.isPending || !projectId}>
            Создать
          </button>
        </div>
      </form>
    </Dialog>
  );
}

/**
 * §7.1: секреты показываются ровно один раз — сервер хранит только хеши
 * и повторно их не отдаст. Закрытие окна — точка невозврата, о чём сказано прямо.
 */
function SecretsDialog({
  response,
  onClose,
}: {
  response: CreateInviteResponse;
  onClose: () => void;
}) {
  return (
    <Dialog
      title={`Приглашение для «${response.invite.projectName}»`}
      onClose={onClose}
      // Esc, клик по подложке и крестик — такая же точка невозврата, как кнопка:
      // до этой правки окно закрывалось ими молча, и секреты пропадали.
      confirmClose="Секреты сохранены? Повторно они не показываются."
    >
      <p className="error">
        Токен и PIN показываются один раз. После закрытия окна получить их снова нельзя — только
        выпустить новое приглашение.
      </p>

      <SecretValue
        title="PIN"
        value={response.secrets.pin}
        ariaLabel="PIN приглашения"
        buttonLabel="Скопировать PIN"
      />

      {response.secrets.links.map((link) => (
        <SecretValue
          key={link.channel}
          title={`Ссылка ${CHANNEL_LABEL[link.channel]}`}
          value={link.url}
          ariaLabel={`Ссылка ${CHANNEL_LABEL[link.channel]}`}
          buttonLabel="Скопировать ссылку"
        />
      ))}

      <div className="form__actions">
        {/* Нажатие на эту кнопку и есть ответ «сохранил», второй раз не спрашиваем */}
        <button type="button" className="btn-primary" onClick={onClose}>
          Готово, секреты сохранены
        </button>
      </div>
    </Dialog>
  );
}
